import React, { useState, useEffect, useRef, useMemo } from 'react';
import api from '../../api/client';
import '../kits/KitPopup.css';
import './WildmicrPopup.css';
import MarketplacesField, { ALL_MARKETPLACES } from './MarketplacesField';
import AlternateIdsField from './AlternateIdsField';
import ProductDetailsField from './ProductDetailsField';

const FertProductPopup = ({ isOpen, onClose, onSaved, editData = null }) => {
    const [form, setForm] = useState({
        SKU: '', productName: '', ASIN: '', FNSKU: '', GTIN: '', brandID: '',
        volumeLb: '', bagsPerBox: '', produce_per_shift: '', machine_packet: 0,
        rawMaterialID: '', niche_category_id: '', isActive: 1,
        marketplaces: ALL_MARKETPLACES,
    });

    const [brands,          setBrands]          = useState([]);
    const [niches,          setNiches]          = useState([]);
    const [allMaterials,    setAllMaterials]    = useState([]);
    const [selectedMat,     setSelectedMat]     = useState(null); // {id, label}
    const [matSearch,       setMatSearch]       = useState('');
    const [matDropOpen,     setMatDropOpen]     = useState(false);
    const [saving, setSaving] = useState(false);
    const [error,  setError]  = useState('');

    const matWrapRef  = useRef(null);
    const matInputRef = useRef(null);
    const [dropStyle, setDropStyle] = useState({});

    useEffect(() => {
        if (!isOpen) return;
        api.get('/api/options/brands').then(r => setBrands(r.data.items || [])).catch(() => {});
        api.get('/api/options/niche_category').then(r => setNiches(r.data.items || [])).catch(() => {});
        api.get('/api/options/fert_raw_materials').then(r => setAllMaterials(r.data.items || [])).catch(() => {});
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        if (editData) {
            setForm({
                SKU:               editData.SKU               || '',
                productName:       editData.productName       || '',
                ASIN:              editData.ASIN              || '',
                FNSKU:             editData.FNSKU             || '',
                GTIN:              editData.GTIN              || '',
                brandID:           editData.brandID           || '',
                volumeLb:          editData.volumeLb          || '',
                bagsPerBox:        editData.bagsPerBox        || '',
                produce_per_shift: editData.produce_per_shift || '',
                machine_packet:    editData.machine_packet ? 1 : 0,
                rawMaterialID:     editData.rawMaterialID     || '',
                niche_category_id: editData.niche_category_id || '',
                isActive:          editData.isActive != null ? editData.isActive : 1,
                marketplaces:      editData.marketplaces != null ? editData.marketplaces : ALL_MARKETPLACES,
            });
        } else {
            setForm({ SKU: '', productName: '', ASIN: '', FNSKU: '', GTIN: '', brandID: '',
                      volumeLb: '', bagsPerBox: '', produce_per_shift: '', machine_packet: 0,
                      rawMaterialID: '', niche_category_id: '', isActive: 1,
                      marketplaces: ALL_MARKETPLACES });
            setSelectedMat(null);
        }
        setMatSearch(''); setMatDropOpen(false); setError('');
    }, [isOpen, editData]);

    // Resolve raw material label once allMaterials loaded
    useEffect(() => {
        if (!editData?.rawMaterialID || !allMaterials.length) return;
        const m = allMaterials.find(x => String(x.id) === String(editData.rawMaterialID));
        setSelectedMat(m ? { id: m.id, label: m.materialName || `#${m.id}` } : null);
    }, [editData, allMaterials]);

    // Outside click
    useEffect(() => {
        const h = (e) => {
            if (matWrapRef.current && !matWrapRef.current.contains(e.target)) setMatDropOpen(false);
        };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    const filteredMaterials = useMemo(() => {
        if (!isOpen) return [];
        const q = matSearch.trim().toLowerCase();
        return allMaterials.filter(m =>
            !q || (m.materialName || '').toLowerCase().includes(q)
        );
    }, [isOpen, allMaterials, matSearch]);

    if (!isOpen) return null;

    const setField = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

    const openDrop = () => {
        if (matInputRef.current) {
            const r = matInputRef.current.getBoundingClientRect();
            setDropStyle({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 300) });
        }
        setMatDropOpen(true);
    };

    const selectMaterial = (m) => {
        setSelectedMat({ id: m.id, label: m.materialName || `#${m.id}` });
        setForm(p => ({ ...p, rawMaterialID: m.id }));
        setMatSearch('');
        setMatDropOpen(false);
    };

    const clearMaterial = () => {
        setSelectedMat(null);
        setForm(p => ({ ...p, rawMaterialID: '' }));
        setMatSearch('');
        setTimeout(() => matInputRef.current?.focus(), 50);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!form.SKU.trim()) { setError('SKU is required'); return; }
        setSaving(true);
        try {
            if (editData) {
                await api.put(`/api/fert-product-popup/${editData.SKU}`, form);
            } else {
                await api.post('/api/fert-product-popup', form);
            }
            onSaved();
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="kit-popup-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="kit-popup wm-popup">

                <div className="kit-popup__header">
                    <div>
                        <div className="kit-popup__title">
                            {editData ? `Edit — ${editData.productName || editData.SKU}` : 'Add Fertilizer Product'}
                        </div>
                        <div className="kit-popup__subtitle">
                            {editData ? 'Update product details and raw material' : 'Fill in product details and select raw material'}
                        </div>
                    </div>
                    <button className="kit-popup__close" type="button" onClick={onClose}>
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><use xlinkHref="#closeBtn" /></svg>
                    </button>
                </div>

                <div className="wm-popup__body">

                    <div className="wm-fields-grid">

                        <div className="kit-field">
                            <span className="kit-field__label">SKU *</span>
                            <input className="kit-field__input" value={form.SKU}
                                readOnly={!!editData} onChange={setField('SKU')} placeholder="e.g. ORFMF_80_01" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Brand</span>
                            <select className="kit-field__input" value={form.brandID}
                                onChange={e => setForm(p => ({ ...p, brandID: e.target.value }))}>
                                <option value="">— Select brand —</option>
                                {brands.map(b => <option key={b.brandID} value={b.brandID}>{b.brandName}</option>)}
                            </select>
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Product Name</span>
                            <input className="kit-field__input" value={form.productName}
                                onChange={setField('productName')} placeholder="Product display name" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Niche</span>
                            <select className="kit-field__input" value={form.niche_category_id}
                                onChange={e => setForm(p => ({ ...p, niche_category_id: e.target.value }))}>
                                <option value="">— Select niche —</option>
                                {niches.map(n => <option key={n.niche_category_id} value={n.niche_category_id}>{n.niche_name}</option>)}
                            </select>
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">ASIN</span>
                            <input className="kit-field__input" value={form.ASIN}
                                onChange={setField('ASIN')} placeholder="Amazon ASIN" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Bags per Box</span>
                            <input className="kit-field__input" type="number" min="1"
                                value={form.bagsPerBox} onChange={setField('bagsPerBox')} placeholder="e.g. 12" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">FNSKU</span>
                            <input className="kit-field__input" value={form.FNSKU}
                                onChange={setField('FNSKU')} placeholder="Amazon FNSKU" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">GTIN</span>
                            <input className="kit-field__input" value={form.GTIN}
                                onChange={setField('GTIN')} placeholder="Barcode / GTIN" />
                        </div>

                        <div className="wm-fields-row">
                            <div className="kit-field" style={{ flex: 1 }}>
                                <span className="kit-field__label">Volume (lb)</span>
                                <input className="kit-field__input" type="number" min="0" step="0.001"
                                    value={form.volumeLb} onChange={setField('volumeLb')} placeholder="e.g. 1.0" />
                            </div>
                            <div className="kit-field" style={{ flex: 1 }}>
                                <span className="kit-field__label">Produce / Shift</span>
                                <input className="kit-field__input" type="number" min="1"
                                    value={form.produce_per_shift} onChange={setField('produce_per_shift')} placeholder="e.g. 120" />
                            </div>
                            <div className="kit-field" style={{ flex: '0 0 90px' }}>
                                <span className="kit-field__label">Active</span>
                                <select className="kit-field__input" value={form.isActive}
                                    onChange={e => setForm(p => ({ ...p, isActive: parseInt(e.target.value) }))}>
                                    <option value={1}>Yes</option>
                                    <option value={0}>No</option>
                                </select>
                            </div>
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label"
                                title="Small packet filled on the Small Bags machine: produced in Small Bags Production, kept loose in finished stock and written off by quantity in kits">
                                Machine Packet
                            </span>
                            <select className="kit-field__input" value={form.machine_packet}
                                onChange={e => setForm(p => ({ ...p, machine_packet: parseInt(e.target.value) }))}>
                                <option value={0}>No</option>
                                <option value={1}>Yes</option>
                            </select>
                        </div>

                        <MarketplacesField
                            value={form.marketplaces}
                            onChange={v => setForm(p => ({ ...p, marketplaces: v }))}
                        />

                        {editData && <ProductDetailsField productType="fert_products" sku={editData.SKU} />}
                        {editData && <AlternateIdsField productType="fert_products" sku={editData.SKU} />}

                    </div>

                    {/* Raw material section */}
                    <div className="wm-seeds-section">
                        <div className="wm-seeds-label">
                            <i className="fas fa-flask" />
                            Raw Material
                        </div>

                        <div className="wm-seeds-row" ref={matWrapRef}>
                            <div className="wm-seed-combo">
                                <i className="fas fa-search wm-seed-combo__icon" />
                                <input
                                    ref={matInputRef}
                                    className={`wm-seed-combo__input${selectedMat ? ' wm-seed-combo__input--selected' : ''}`}
                                    value={selectedMat ? selectedMat.label : matSearch}
                                    readOnly={!!selectedMat}
                                    placeholder="Search raw material…"
                                    onChange={e => { setMatSearch(e.target.value); setMatDropOpen(true); }}
                                    onFocus={openDrop}
                                    onClick={() => { if (!selectedMat) openDrop(); }}
                                />
                                {selectedMat && (
                                    <button type="button" className="wm-seed-combo__clear" onClick={clearMaterial} title="Clear">
                                        <i className="fas fa-times" />
                                    </button>
                                )}
                                {matDropOpen && !selectedMat && (
                                    <div className="set-comp__dropdown" style={dropStyle}>
                                        {filteredMaterials.length === 0
                                            ? <div className="set-comp__dropdown-empty">{matSearch.trim() ? 'No materials found' : 'Start typing…'}</div>
                                            : filteredMaterials.map(m => (
                                                <div key={m.id} className="set-comp__dropdown-item" onMouseDown={() => selectMaterial(m)}>
                                                    <span className="set-comp__item-name">{m.materialName}</span>
                                                    <span className="set-comp__item-sku">#{m.id}</span>
                                                </div>
                                            ))
                                        }
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                </div>

                <div className="kit-popup__footer">
                    <span className="kit-popup__error">{error && `⚠ ${error}`}</span>
                    <button className="kit-popup__submit" onClick={handleSubmit} disabled={saving}>
                        {saving ? 'Saving…' : (editData ? 'Save Changes' : 'Create Product')}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default FertProductPopup;
